import React, { Component } from 'react';
import CircularProgressbar from 'react-circular-progressbar';
import c from 'classnames';
import FCIcon from '@lyearn-team/frontend-components/dist/Icon';
import Icon from 'helper/Icon';
import CustomDotDotDot from 'helper/customDotDotDot';
import { withTheme } from 'emotion-theming';
import { Tooltip } from 'antd';
import ParticipantCompletion from 'components/LiveClass/model-views/ParticipantCompletion';
import { observer } from 'mobx-react';
import styles from './styles';

const kContentIconSize = 17.0;

@withTheme
@observer
class CardCell extends Component {
  getCardStatus = card => {
    if (!card.performance) return null;
    const gradable = card.isGradable;
    const completionStatus = card.performance && card.performance.completionStatus;
    const successStatus = card.performance.successStatus;
    if ((!gradable && completionStatus === 'COMPLETE') || (gradable && successStatus === 'PASS'))
      return 'COMPLETE';
    if (gradable && completionStatus === 'COMPLETE' && successStatus === 'FAIL') return 'FAILED';
    if (gradable && completionStatus === 'COMPLETE' && successStatus === 'NA') return 'AWAITING';

    return null;
  };

  cardTapped = e => this.props.cardTapped(this.props.cardInfo.pid, e);

  renderProgressCircle() {
    return (
      <div
        style={{
          ...styles.column,
          opacity: this.props.isAccessible ? 1 : 0.45
        }}
      >
        {this.renderIcon(this.props.cardInfo.resource.type, '#8392a7')}
      </div>
    );
  }

  renderCardProgress = (active, isInSync) => {
    if (!this.props.isAccessible) {
      return (
        <Icon
          icon="lock-icon"
          size="17.4px"
          fill="var(--color-black3)"
          viewBox="0 0 36 46"
          style={{ opacity: 0.5 }}
        />
      );
    }

    const status = this.getCardStatus(this.props.cardInfo);
    const checkIcon = (
      <Icon icon="check" size="10" viewBox="1 1 22 22" fill="#ffffff" style={{ float: 'left' }} />
    );
    const playIcon = (
      <Icon
        icon="play-icon"
        size="13"
        viewBox="-10 -10 100 125"
        fill="#ffffff"
        style={{ float: 'left' }}
      />
    );
    const iconClassName = c('cardStatusIcon', { active });
    const hideForProgress = this.props.cardInfo.completionType === 'OPTIONAL' && !active;
    if (status === 'COMPLETE') {
      return (
        <div
          className={iconClassName}
          style={
            isInSync && window.debugModeTrue ? styles.isSyncCompletedIcon : styles.completedIcon
          }
        >
          {active ? playIcon : checkIcon}
        </div>
      );
    }
    if (status === 'FAILED') {
      return (
        <div className={iconClassName} style={styles.failedIcon}>
          !
        </div>
      );
    }
    if (status === 'AWATING') {
      return (
        <div className={iconClassName} style={styles.awaitingIcon}>
          {active ? playIcon : checkIcon}
        </div>
      );
    }

    const percentage = this.props.cardInfo.performance
      ? this.props.cardInfo.performance.scaledProgressMeasure * 100
      : 0;

    return (
      <div className="inProgressIconWrapper">
        <span className="inProgressIconContainer">{active ? playIcon : null}</span>
        {!hideForProgress && (
          <CircularProgressbar
            className={
              'CircularProgressbarSVG' +
              (active ? ' active' : '') +
              (isInSync && window.debugModeTrue ? ' isInSync' : '')
            }
            percentage={percentage}
            strokeWidth={9}
            initialAnimation={false}
          />
        )}
      </div>
    );
  };

  renderIcon(type, iconColor) {
    switch (type) {
      case 'video':
        return (
          <Icon
            className="cardIcon"
            icon="lt-video"
            size={kContentIconSize}
            fill={iconColor}
            viewBox="0 0 18 12"
          />
        );
      case 'audio':
        return (
          <Icon
            className="cardIcon"
            icon="lt-audio"
            size={kContentIconSize}
            fill={iconColor}
            viewBox="0 0 12 19"
          />
        );
      case 'html':
        return (
          <Icon
            className="cardIcon"
            icon="lt-1"
            size={kContentIconSize}
            fill={iconColor}
            viewBox="0 0 18 18"
          />
        );
      case 'pdf':
        return <Icon icon="lt-url" size={kContentIconSize} fill={iconColor} viewBox="0 0 15 15" />;
      case 'normal':
      case 'assignment':
      case 'peerReview':
        return (
          <Icon
            className="cardIcon"
            icon="lt-assignment"
            size="17px"
            fill={iconColor}
            viewBox="0 0 16 16"
          />
        );
      case 'quiz':
        return (
          <Icon
            className="cardIcon"
            icon="lt-assignment"
            size="13px"
            fill={iconColor}
            viewBox="0 0 16 16"
          />
        );
      case 'scorm':
        return (
          <Icon className="cardIcon" icon="lt-scorm" viewBox="0 0 17 16" size="15px" fill="#fff" />
        );
      case 'blocks':
        return (
          <Icon className="cardIcon" viewBox="0 0 18 18" icon="lt-1" size="15px" fill={iconColor} />
        );
      default:
        return (
          <Icon className="cardIcon" icon="assignment-card-icon" size="17px" fill={iconColor} />
        );
    }
  }

  renderUsersToolTip = participantCompletionInfo => () => {
    const {
      completedCount,
      incompleteCount,
      completedLearners,
      incompleteLearners
    } = participantCompletionInfo;
    const completedLearnersText = completedLearners
      ? completedLearners.map(u => u.name).join(',')
      : '...';
    const incompleteLearnersText = incompleteLearners
      ? incompleteLearners.map(u => u.name).join(',')
      : '...';

    return (
      <div>
        {completedCount > 0 && (
          <div>
            Completed:
            {completedLearnersText}
          </div>
        )}
        {incompleteCount > 0 && (
          <div>
            Incomplete:
            {incompleteLearnersText}
          </div>
        )}
      </div>
    );
  };

  renderLiveClassParticipantProgress = participantCompletionInfo => {
    const { theme } = this.props;
    const { completedCount, totalCount } = participantCompletionInfo;

    return (
      <span className={styles.participantProgressContainer}>
        <FCIcon type="users" className={styles.usersIcon(theme)} />
        <Tooltip title={totalCount > 0 ? this.renderUsersToolTip(participantCompletionInfo) : null}>
          <span className={styles.completedUsersText}>
            {completedCount} of
            {totalCount} completed
          </span>
        </Tooltip>
      </span>
    );
  };

  render() {
    const isOptional = this.props.cardInfo.completionType === 'OPTIONAL';
    const isInSync = this.props.isInSync;

    return (
      <div
        onClick={e => this.cardTapped(e)}
        className={c(styles.container, { currentCardCell: this.props.isCurrent })}
        role="button"
        tabIndex="0"
      >
        <div
          className={
            'cardInfoContainer' +
            (this.props.isCurrent ? ' active' : '') +
            (!this.props.isAccessible ? ' isNotAccessible' : '')
          }
        >
          {this.renderProgressCircle()}
          <div style={{ ...styles.centerContent, opacity: this.props.isAccessible ? 1 : 0.75 }}>
            <CustomDotDotDot keep=".timeLabel" height={39} comparer={this.props.cardInfo.name}>
              <div title={this.props.cardInfo.name} style={styles.cardName}>
                {this.props.cardInfo.globalPosition + ' ' + this.props.cardInfo.name}
                {isOptional && (
                  <span className="timeLabel " style={styles.optioalLabel}>
                    (optional)
                  </span>
                )}
                {!isOptional && this.props.cardInfo.resource.type !== 'objective' ? (
                  <span className="timeLabel" style={styles.timeLabel}>
                    {this.props.cardInfo.duration > 59
                      ? Math.round(this.props.cardInfo.duration / 60) + ' min'
                      : Math.round(this.props.cardInfo.duration) + ' sec'}
                  </span>
                ) : null}
              </div>
            </CustomDotDotDot>
            <ParticipantCompletion cardId={this.props.cardInfo.pid}>
              {this.renderLiveClassParticipantProgress}
            </ParticipantCompletion>
          </div>
          <div style={styles.rightContent}>
            {this.renderCardProgress(this.props.isCurrent, isInSync)}
          </div>
        </div>
      </div>
    );
  }
}

export default CardCell;
